/**
 * GestaoPedidosTab - Componente para gestão de pedidos/envios
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '@/hooks/use-toast';

const GestaoPedidosTab = ({ envios = [], pedidos = [], cotacoes: cotacoesGlobais = [], produtos = [], onRefresh, usuario, showConfirmDialog }) => {
  // Helper para verificar se usuário tem permissões de Comprador/Administrador
  const temPermissaoComprador = () => {
    return usuario.tipo_usuario === 'Comprador' || usuario.tipo_usuario === 'Administrador';
  };

  const [showCriarModal, setShowCriarModal] = useState(false);
  const [showVisualizarModal, setShowVisualizarModal] = useState(false);
  const [envioSelecionado, setEnvioSelecionado] = useState(null);
  const [pedidosSelecionados, setPedidosSelecionados] = useState([]);
  const [titulo, setTitulo] = useState('');
  const [produtosConsolidados, setProdutosConsolidados] = useState([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  const [ordenacaoFornecedor, setOrdenacaoFornecedor] = useState(false);
  const [filtraMeiaCaixa, setFiltraMeiaCaixa] = useState(false);
  const [produtosExpandidos, setProdutosExpandidos] = useState({}); // Track which products have expanded filiais view
  const [searchTerm, setSearchTerm] = useState(''); // Termo de pesquisa
  const [cotacoes, setCotacoes] = useState([]); // Lista de cotações
  const [cotacaoSelecionada, setCotacaoSelecionada] = useState(''); // ID da cotação selecionada
  const [modoEdicao, setModoEdicao] = useState(false); // Controla se está em modo de edição
  const [quantidadesEditadas, setQuantidadesEditadas] = useState({}); // Armazena quantidades editadas temporariamente
  const [meiaCaixaEditada, setMeiaCaixaEditada] = useState({}); // Armazena informações de meia caixa editadas
  const [salvandoEdicao, setSalvandoEdicao] = useState(false); // Loading ao salvar
  const [todasFiliais, setTodasFiliais] = useState([]); // Lista de todas as filiais
  
  // Estados para modal de sugestões de ajuste
  const [showSugestoesModal, setShowSugestoesModal] = useState(false);
  const [sugestoes, setSugestoes] = useState(null);  // Dados das sugestões
  const [sugestoesSelecionadas, setSugestoesSelecionadas] = useState({});  // {produto_id: {pedido_id: boolean}}
  const [loadingSugestoes, setLoadingSugestoes] = useState(false);
  const [aplicandoAjustes, setAplicandoAjustes] = useState(false);

  // Filtrar apenas pedidos finalizados
  const pedidosFinalizados = pedidos.filter(p => p.finalizado);
  
  // Função utilitária para buscar todos os registros com paginação
  const fetchAllPaginated = async (endpoint, limit = 1000) => {
    let allItems = [];
    let skip = 0;
    let hasMore = true;
    
    while (hasMore) {
      const response = await axios.get(`${endpoint}?skip=${skip}&limit=${limit}`);
      const data = response.data;
      
      if (data.items) {
        allItems = [...allItems, ...data.items];
        hasMore = data.has_more || false;
        skip += limit;
      } else if (Array.isArray(data)) {
        allItems = data;
        hasMore = false;
      } else {
        allItems = [];
        hasMore = false;
      }
    }
    
    return allItems;
  };
  
  // Carregar cotações quando abrir modal
  const loadCotacoes = async () => {
    try {
      const cotacoesData = await fetchAllPaginated('/cotacoes');
      setCotacoes(cotacoesData);
    } catch (error) {
      console.error('Erro ao carregar cotações:', error);
    }
  };
  
  // Abrir modal de criar envio
  const handleAbrirCriarModal = () => {
    setShowCriarModal(true);
    loadCotacoes();
  };

  // Função para verificar sugestões antes de criar envio
  const handleVerificarSugestoes = async () => {
    if (!titulo.trim()) {
      toast({ title: "Digite um título para o envio", variant: "destructive" });
      return;
    }
    if (pedidosSelecionados.length === 0) {
      toast({ title: "Selecione pelo menos um pedido", variant: "destructive" });
      return;
    }

    // Se apenas 1 pedido selecionado, criar diretamente
    if (pedidosSelecionados.length === 1) {
      await handleCriarEnvioFinal();
      return;
    }

    // Verificar se há sugestões de ajuste
    try {
      setLoadingSugestoes(true);
      const response = await axios.post('/envios/calcular-sugestoes', {
        pedidos_ids: pedidosSelecionados
      });
      
      if (response.data.tem_sugestoes) {
        setSugestoes(response.data);
        // Inicializar apenas a melhor sugestão como selecionada por padrão
        const selecionadas = {};
        response.data.produtos.forEach(produto => {
          if (produto.melhor_sugestao) {
            selecionadas[produto.produto_id] = produto.melhor_sugestao.pedido_id;
          }
        });
        setSugestoesSelecionadas(selecionadas);
        setShowSugestoesModal(true);
      } else {
        // Sem sugestões, criar envio diretamente
        await handleCriarEnvioFinal();
      }
    } catch (error) {
      console.error('Erro ao calcular sugestões:', error);
      // Em caso de erro, criar envio sem sugestões
      await handleCriarEnvioFinal();
    } finally {
      setLoadingSugestoes(false);
    }
  };

  // Aplicar sugestões selecionadas e criar envio
  const handleAplicarSugestoes = async () => {
    try {
      setAplicandoAjustes(true);
      
      // Coletar ajustes selecionados (apenas UMA sugestão por produto)
      const ajustes = [];
      sugestoes?.produtos.forEach(produto => {
        const pedidoSelecionado = sugestoesSelecionadas[produto.produto_id];
        if (pedidoSelecionado) {
          // Encontrar a sugestão selecionada
          let sugestaoSelecionada = null;
          if (produto.melhor_sugestao?.pedido_id === pedidoSelecionado) {
            sugestaoSelecionada = produto.melhor_sugestao;
          } else {
            sugestaoSelecionada = produto.alternativas?.find(s => s.pedido_id === pedidoSelecionado);
          }
          
          if (sugestaoSelecionada) {
            ajustes.push({
              pedido_id: sugestaoSelecionada.pedido_id,
              produto_id: sugestaoSelecionada.produto_id,
              nova_quantidade: sugestaoSelecionada.quantidade_sugerida
            });
          }
        }
      });
      
      // Aplicar ajustes se houver
      if (ajustes.length > 0) {
        await axios.post('/envios/aplicar-ajustes', { ajustes });
        toast({ title: `${ajustes.length} ajuste(s) aplicado(s) nos pedidos!` });
      }
      
      // Fechar modal de sugestões
      setShowSugestoesModal(false);
      setSugestoes(null);
      
      // Criar envio
      await handleCriarEnvioFinal();
      
    } catch (error) {
      console.error('Erro ao aplicar ajustes:', error);
      toast({
        title: "Erro ao aplicar ajustes",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setAplicandoAjustes(false);
    }
  };

  // Pular sugestões e criar envio
  const handlePularSugestoes = async () => {
    setShowSugestoesModal(false);
    setSugestoes(null);
    await handleCriarEnvioFinal();
  };

  // Criar envio final (após verificar/aplicar sugestões)
  const handleCriarEnvioFinal = async () => {
    try {
      await axios.post('/envios', {
        titulo: titulo.trim(),
        pedidos_ids: pedidosSelecionados,
        cotacao_id: cotacaoSelecionada || null
      });
      toast({ title: "Envio criado com sucesso!" });
      setShowCriarModal(false);
      setTitulo('');
      setPedidosSelecionados([]);
      setCotacaoSelecionada('');
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao criar envio",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função legada para manter compatibilidade (não usada diretamente)
  const handleCriarEnvio = async () => {
    await handleVerificarSugestoes();
  };

  const handleExcluirEnvio = async (envio) => {
    const confirmed = await showConfirmDialog(
      "Excluir Envio",
      `Tem certeza que deseja excluir "${envio.titulo}"? Esta ação não poderá ser desfeita.`
    );
    
    if (!confirmed) return;

    try {
      await axios.delete(`/envios/${envio.id}`);
      toast({ title: "Envio excluído com sucesso!" });
      onRefresh();
    } catch (error) {
      toast({
        title: "Erro ao excluir envio",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    }
  };

  // Função para iniciar modo de edição
  const handleIniciarEdicao = () => {
    setModoEdicao(true);
    
    // Extrair todas as filiais únicas dos pedidos do envio
    const pedidosDoEnvio = pedidos.filter(p => envioSelecionado.pedidos_ids.includes(p.id));
    const filiaisUnicas = [...new Set(pedidosDoEnvio.map(p => p.filial))].sort();
    setTodasFiliais(filiaisUnicas);
    
    // Inicializar quantidades editadas com valores atuais
    const quantidades = {};
    const meiaCaixa = {};
    
    produtosConsolidados.forEach(produto => {
      produto.pedidos.forEach(pedidoInfo => {
        const key = `${pedidoInfo.pedido_id}_${produto.produto_id}`;
        quantidades[key] = pedidoInfo.quantidade;
        
        // Buscar informações de meia caixa do pedido original
        const pedidoOriginal = pedidos.find(p => p.id === pedidoInfo.pedido_id);
        if (pedidoOriginal) {
          const produtoPedido = pedidoOriginal.produtos.find(p => p.produto_id === produto.produto_id);
          if (produtoPedido) {
            // Sempre adicionar as informações, mesmo que alguns campos sejam null/undefined
            meiaCaixa[key] = {
              pediu_meia_cx: produtoPedido.pediu_meia_cx || null,
              qualidade: produtoPedido.qualidade || null,
              qtd_verde: produtoPedido.qtd_verde || null,
              qtd_maduro: produtoPedido.qtd_maduro || null
            };
          }
        }
      });
    });
    
    setQuantidadesEditadas(quantidades);
    setMeiaCaixaEditada(meiaCaixa);
  };

  // Função para validar quantidade (apenas inteiros ou meios)
  const validarQuantidade = (valor) => {
    if (valor === '' || valor === null || valor === undefined) return true; // Permite vazio para remoção
    const num = parseFloat(valor);
    if (isNaN(num)) return false;
    // Verifica se é inteiro ou termina em .5
    const decimal = num - Math.floor(num);
    return decimal === 0 || decimal === 0.5;
  };

  // Função para cancelar edição
  const handleCancelarEdicao = () => {
    setModoEdicao(false);
    setQuantidadesEditadas({});
    setMeiaCaixaEditada({});
  };

  // Função para atualizar quantidade editada
  const handleQuantidadeChange = (pedidoId, produtoId, novaQuantidade) => {
    const key = `${pedidoId}_${produtoId}`;
    
    // Permitir vazio (para remoção)
    if (novaQuantidade === '' || novaQuantidade === null) {
      setQuantidadesEditadas(prev => ({
        ...prev,
        [key]: null // null indica remoção
      }));
      return;
    }
    
    // Validar quantidade
    if (!validarQuantidade(novaQuantidade)) {
      toast({
        title: "Quantidade inválida",
        description: "Apenas números inteiros ou meios (0.5, 1.5, 2.5...) são permitidos",
        variant: "destructive"
      });
      return;
    }
    
    setQuantidadesEditadas(prev => ({
      ...prev,
      [key]: parseFloat(novaQuantidade)
    }));
  };

  // Função para atualizar informações de meia caixa
  const handleMeiaCaixaChange = (pedidoId, produtoId, campo, valor) => {
    const key = `${pedidoId}_${produtoId}`;
    setMeiaCaixaEditada(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [campo]: valor
      }
    }));
  };

  // Função para salvar alterações
  const handleSalvarEdicao = async () => {
    setSalvandoEdicao(true);
    
    try {
      // Agrupar alterações por pedido
      const alteracoesPorPedido = {};
      
      // Coletar todas as keys que foram editadas (quantidade OU meia caixa)
      const todasKeys = new Set([
        ...Object.keys(quantidadesEditadas),
        ...Object.keys(meiaCaixaEditada)
      ]);
      
      todasKeys.forEach(key => {
        const [pedidoId, produtoId] = key.split('_');
        
        if (!alteracoesPorPedido[pedidoId]) {
          alteracoesPorPedido[pedidoId] = {
            produtosExistentes: [],
            produtosNovos: []
          };
        }
        
        // Verificar se o produto já existe no pedido
        const pedidoCompleto = pedidos.find(p => p.id === pedidoId);
        const produtoExiste = pedidoCompleto?.produtos.some(p => p.produto_id === produtoId);
        
        // Buscar produto original para preservar valores não editados
        const produtoOriginal = pedidoCompleto?.produtos.find(p => p.produto_id === produtoId);
        
        const meiaCaixaInfo = meiaCaixaEditada[key] || {};
        const quantidadeEditada = quantidadesEditadas.hasOwnProperty(key) 
          ? quantidadesEditadas[key] 
          : produtoOriginal?.quantidade;
        
        const dadosProduto = {
          produto_id: produtoId,
          quantidade: quantidadeEditada,
          // Preservar valores originais se não foram editados
          pediu_meia_cx: meiaCaixaInfo.pediu_meia_cx !== undefined 
            ? meiaCaixaInfo.pediu_meia_cx 
            : produtoOriginal?.pediu_meia_cx,
          qualidade: meiaCaixaInfo.qualidade !== undefined 
            ? meiaCaixaInfo.qualidade 
            : produtoOriginal?.qualidade,
          qtd_verde: meiaCaixaInfo.qtd_verde !== undefined 
            ? meiaCaixaInfo.qtd_verde 
            : produtoOriginal?.qtd_verde,
          qtd_maduro: meiaCaixaInfo.qtd_maduro !== undefined 
            ? meiaCaixaInfo.qtd_maduro 
            : produtoOriginal?.qtd_maduro
        };
        
        if (produtoExiste) {
          alteracoesPorPedido[pedidoId].produtosExistentes.push(dadosProduto);
        } else {
          alteracoesPorPedido[pedidoId].produtosNovos.push(dadosProduto);
        }
      });

      // Atualizar cada pedido
      const promises = Object.entries(alteracoesPorPedido).map(async ([pedidoId, dados]) => {
        const pedidoCompleto = pedidos.find(p => p.id === pedidoId);
        if (!pedidoCompleto) return;

        // Processar produtos: remover com quantidade null, atualizar existentes, adicionar novos
        let produtosAtualizados = pedidoCompleto.produtos
          .map(prod => {
            const produtoEditado = dados.produtosExistentes.find(p => p.produto_id === prod.produto_id);
            if (produtoEditado) {
              // Se quantidade é null, retornar null para remover depois
              if (produtoEditado.quantidade === null) {
                return null;
              }
              // Atualizar com novas informações
              return {
                ...prod,
                quantidade: produtoEditado.quantidade,
                pediu_meia_cx: produtoEditado.pediu_meia_cx !== undefined ? produtoEditado.pediu_meia_cx : prod.pediu_meia_cx,
                qualidade: produtoEditado.qualidade !== undefined ? produtoEditado.qualidade : prod.qualidade,
                qtd_verde: produtoEditado.qtd_verde !== undefined ? produtoEditado.qtd_verde : prod.qtd_verde,
                qtd_maduro: produtoEditado.qtd_maduro !== undefined ? produtoEditado.qtd_maduro : prod.qtd_maduro
              };
            }
            return prod;
          })
          .filter(p => p !== null); // Remover produtos com quantidade null

        // Adicionar produtos novos (de outras filiais)
        dados.produtosNovos.forEach(novoProduto => {
          if (novoProduto.quantidade !== null && novoProduto.quantidade > 0) {
            const produtoInfo = produtos.find(p => p.id === novoProduto.produto_id);
            produtosAtualizados.push({
              produto_id: novoProduto.produto_id,
              nome_produto: produtoInfo?.nome || 'N/A',
              quantidade: novoProduto.quantidade,
              pediu_meia_cx: novoProduto.pediu_meia_cx || null,
              qualidade: novoProduto.qualidade || '—',
              qtd_verde: novoProduto.qtd_verde || null,
              qtd_maduro: novoProduto.qtd_maduro || null,
              valor: null
            });
          }
        });

        // Enviar atualização ao backend
        return axios.put(`/pedidos/${pedidoId}/produtos`, produtosAtualizados);
      });

      await Promise.all(promises);

      toast({ 
        title: "Alterações salvas com sucesso!",
        description: "Os pedidos foram atualizados"
      });

      // Recarregar dados
      await onRefresh();
      
      // Reconsolidar produtos com novos dados
      const produtosConsolidadosArray = consolidarProdutos(envioSelecionado);
      setProdutosConsolidados(produtosConsolidadosArray);
      
      // Sair do modo de edição
      setModoEdicao(false);
      setQuantidadesEditadas({});
      setMeiaCaixaEditada({});
      
    } catch (error) {
      toast({
        title: "Erro ao salvar alterações",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const consolidarProdutos = (envio) => {
    const produtosMap = {};
    const pedidosDoEnvio = pedidos.filter(p => envio.pedidos_ids.includes(p.id));
    
    // Para COMPRADOR: consolidar produtos dos pedidos
    if (temPermissaoComprador()) {
      pedidosDoEnvio.forEach(pedido => {
        if (!pedido.produtos || pedido.produtos.length === 0) return;

        pedido.produtos.forEach(prod => {
          if (!produtosMap[prod.produto_id]) {
            let cotacaoComProduto = null;
            
            // Se o envio tem uma cotação vinculada, usar ela prioritariamente
            if (envio.cotacao_id) {
              cotacaoComProduto = cotacoesGlobais.find(c => c.id === envio.cotacao_id);
            }
            
            // Se não encontrou cotação vinculada ou não existe, buscar última cotação encerrada
            if (!cotacaoComProduto) {
              cotacaoComProduto = cotacoesGlobais
                .filter(c => c.status_cotacao === 'Encerrada' && c.vencedores && c.vencedores[prod.produto_id])
                .sort((a, b) => new Date(b.criado_em) - new Date(a.criado_em))[0];
            }

            const vencedor = cotacaoComProduto?.vencedores?.[prod.produto_id];
            
            // Buscar detalhes do produto para verificar unidade
            const produtoDetalhes = produtos.find(p => p.id === prod.produto_id);
            
            // Buscar variação vencedora se existir
            const variacaoVencedora = cotacaoComProduto?.variacoes_vencedoras?.[prod.produto_id];
            const nomeProdutoComVariacao = variacaoVencedora 
              ? `${prod.nome_produto} - ${variacaoVencedora}`
              : prod.nome_produto;
            
            let valorKg = 0;
            let valorCaixa = 0;
            
            if (vencedor) {
              if (produtoDetalhes && produtoDetalhes.unidade === 'UN') {
                // Produto UN: usar preco_kg_un diretamente
                valorKg = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
                valorCaixa = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
              } else {
                // Produto CX/SC: calcular a partir de valor_caixa / quantidade_kg
                valorKg = vencedor.quantidade_kg 
                  ? (vencedor.valor_caixa / vencedor.quantidade_kg) 
                  : 0;
                valorCaixa = vencedor.valor_caixa || 0;
              }
            }

            produtosMap[prod.produto_id] = {
              produto_id: prod.produto_id,
              nome: nomeProdutoComVariacao,
              quantidade: 0,
              custo: valorKg,
              custo_cx: valorCaixa,
              fornecedor: vencedor?.nome_fornecedor || 'N/A',
              qtd_verde: 0,
              qtd_maduro: 0,
              pedidos: [], // Array para armazenar info de cada pedido
              unidade: produtoDetalhes?.unidade || 'CX' // Incluir unidade do produto
            };
          }
          
          const quantidade = parseFloat(prod.quantidade || 0);
          produtosMap[prod.produto_id].quantidade += quantidade;
          
          // Adicionar informação do pedido
          produtosMap[prod.produto_id].pedidos.push({
            pedido_id: pedido.id,
            pedido_numero: pedido.numero,
            filial: pedido.filial || pedido.filial_id || 'N/A',
            quantidade: quantidade
          });
          
          // Consolidar qualidades
          if (prod.qualidade === 'Tudo verde') {
            produtosMap[prod.produto_id].qtd_verde += quantidade;
          } else if (prod.qualidade === 'Tudo maduro') {
            produtosMap[prod.produto_id].qtd_maduro += quantidade;
          } else if (prod.qualidade === 'Ambos') {
            produtosMap[prod.produto_id].qtd_verde += parseFloat(prod.qtd_verde || 0);
            produtosMap[prod.produto_id].qtd_maduro += parseFloat(prod.qtd_maduro || 0);
          }
        });
      });
    } 
    // Para FORNECEDOR: mostrar apenas produtos onde ele ganhou nas cotações E que estão neste envio
    else if (usuario.tipo_usuario === 'Fornecedor') {
      const produtosDoEnvio = new Set();
      
      pedidosDoEnvio.forEach(pedido => {
        if (pedido.produtos && pedido.produtos.length > 0) {
          pedido.produtos.forEach(prod => {
            produtosDoEnvio.add(prod.produto_id);
          });
        }
      });
      
      let cotacoesEncerradas = [];
      
      // Se o envio tem uma cotação vinculada, usar apenas ela
      if (envio.cotacao_id) {
        const cotacaoVinculada = cotacoesGlobais.find(c => c.id === envio.cotacao_id);
        if (cotacaoVinculada && cotacaoVinculada.vencedores) {
          cotacoesEncerradas = [cotacaoVinculada];
        }
      } else {
        // Caso contrário, buscar todas as cotações encerradas
        cotacoesEncerradas = cotacoesGlobais.filter(c => 
          c.status_cotacao === 'Encerrada' && 
          c.vencedores && 
          Object.keys(c.vencedores).length > 0
        );
      }
      
      cotacoesEncerradas.forEach(cotacao => {
        Object.entries(cotacao.vencedores || {}).forEach(([produtoId, vencedor]) => {
          // Verificar se este fornecedor ganhou E se o produto está nos pedidos deste envio
          if (vencedor.nome_fornecedor === usuario.nome_completo && produtosDoEnvio.has(produtoId)) {
            // Buscar nome do produto
            const produtoDetalhes = produtos.find(p => p.id === produtoId);
            const produtoNomeBase = produtoDetalhes?.nome || `Produto ${produtoId.slice(0, 8)}`;
            
            // Buscar variação vencedora se existir
            const variacaoVencedora = cotacao.variacoes_vencedoras?.[produtoId];
            const produtoNome = variacaoVencedora 
              ? `${produtoNomeBase} - ${variacaoVencedora}`
              : produtoNomeBase;
            
            // Calcular custo baseado no tipo de produto
            let valorKg = 0;
            let valorCaixa = 0;
            
            if (produtoDetalhes && produtoDetalhes.unidade === 'UN') {
              // Produto UN: usar preco_kg_un
              valorKg = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
              valorCaixa = vencedor.preco_kg_un || vencedor.valor_caixa || 0;
            } else {
              // Produto CX/SC: calcular
              valorKg = vencedor.quantidade_kg 
                ? (vencedor.valor_caixa / vencedor.quantidade_kg) 
                : 0;
              valorCaixa = vencedor.valor_caixa || 0;
            }
            
            // Calcular quantidade total deste produto nos pedidos do envio
            let quantidadeTotal = 0;
            let qtdVerde = 0;
            let qtdMaduro = 0;
            const pedidosInfo = [];
            
            pedidosDoEnvio.forEach(pedido => {
              if (pedido.produtos) {
                pedido.produtos.forEach(prod => {
                  if (prod.produto_id === produtoId) {
                    const quantidade = parseFloat(prod.quantidade || 0);
                    quantidadeTotal += quantidade;
                    
                    pedidosInfo.push({
                      pedido_id: pedido.id,
                      pedido_numero: pedido.numero,
                      filial: pedido.filial || pedido.filial_id || 'N/A',
                      quantidade: quantidade
                    });
                    
                    // Consolidar qualidades
                    if (prod.qualidade === 'Tudo verde') {
                      qtdVerde += quantidade;
                    } else if (prod.qualidade === 'Tudo maduro') {
                      qtdMaduro += quantidade;
                    } else if (prod.qualidade === 'Ambos') {
                      qtdVerde += parseFloat(prod.qtd_verde || 0);
                      qtdMaduro += parseFloat(prod.qtd_maduro || 0);
                    }
                  }
                });
              }
            });
            
            if (!produtosMap[produtoId]) {
              produtosMap[produtoId] = {
                produto_id: produtoId,
                nome: produtoNome,
                quantidade: quantidadeTotal,
                custo: valorKg,
                custo_cx: valorCaixa,
                fornecedor: usuario.nome_completo,
                qtd_verde: qtdVerde,
                qtd_maduro: qtdMaduro,
                pedidos: pedidosInfo,
                unidade: produtoDetalhes?.unidade || 'CX' // Incluir unidade do produto
              };
            }
          }
        });
      });
    }

    let produtosArray = Object.values(produtosMap);
    return produtosArray;
  };

  const handleVisualizar = async (envio) => {
    setEnvioSelecionado(envio);
    setShowVisualizarModal(true);
    setLoadingProdutos(true);
    setOrdenacaoFornecedor(false); // Reset ordenação
    setFiltraMeiaCaixa(false); // Reset filtro
    setProdutosExpandidos({}); // Reset produtos expandidos
    setSearchTerm(''); // Reset pesquisa
    
    try {
      // Consolidar produtos usando a função local que já inclui qualidade e pedidos
      const produtosConsolidadosArray = consolidarProdutos(envio);
      setProdutosConsolidados(produtosConsolidadosArray);
    } catch (error) {
      toast({
        title: "Erro ao carregar produtos",
        description: error.response?.data?.detail || "Erro interno",
        variant: "destructive"
      });
      setProdutosConsolidados([]);
    } finally {
      setLoadingProdutos(false);
    }
  };


  const getProdutosOrdenados = () => {
    let produtosFiltrados = produtosConsolidados;
    
    // Aplicar filtro de pesquisa se houver termo de busca
    if (searchTerm.trim()) {
      produtosFiltrados = produtosFiltrados.filter(p => 
        p.nome.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Aplicar filtro de meia caixa se ativado
    if (filtraMeiaCaixa) {
      produtosFiltrados = produtosFiltrados.filter(p => {
        // Verificar se a quantidade tem parte fracionária (não é um número inteiro)
        return p.quantidade % 1 !== 0;
      });
    }
    
    // Aplicar ordenação alfabética por nome de produto (padrão)
    let produtosOrdenados = [...produtosFiltrados].sort((a, b) => {
      return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
    });
    
    // Se ordenação por fornecedor estiver ativa, aplicar
    if (ordenacaoFornecedor) {
      produtosOrdenados = produtosOrdenados.sort((a, b) => {
        return a.fornecedor.localeCompare(b.fornecedor, 'pt-BR', { sensitivity: 'base' });
      });
    }
    
    return produtosOrdenados;
  };


  const exportarParaImpressao = () => {
    if (!envioSelecionado || produtosConsolidados.length === 0) return;
    
    const printWindow = window.open('', '_blank');
    
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${envioSelecionado.titulo}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #4f46e5; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #4f46e5; color: white; padding: 12px; text-align: left; }
          td { border: 1px solid #ddd; padding: 10px; }
          tr:nth-child(even) { background-color: #f9fafb; }
        </style>
      </head>
      <body>
        <h1>${envioSelecionado.titulo}</h1>
        <p>Criado por: ${envioSelecionado.criado_por_nome}</p>
        <p>Data: ${new Date(envioSelecionado.criado_em).toLocaleString('pt-BR')}</p>
        <table>
          <thead>
            <tr>
              <th>PRODUTO</th>
              <th>QUANTIDADE</th>
              <th>CUSTO (R$/KG)</th>
              <th>CUSTO CX</th>
              <th>FORNECEDOR</th>
            </tr>
          </thead>
          <tbody>
            ${getProdutosOrdenados().map(p => {
              let qualidadeInfo = '';
              if (p.qtd_verde > 0 && p.qtd_maduro > 0) {
                qualidadeInfo = `<br/><span style="font-size: 10pt; color: #059669;">VERDE: ${p.qtd_verde.toFixed(2)}</span> • <span style="font-size: 10pt; color: #c2410c;">MADURO: ${p.qtd_maduro.toFixed(2)}</span>`;
              } else if (p.qtd_verde > 0) {
                qualidadeInfo = `<br/><span style="font-size: 10pt; color: #059669;">TUDO VERDE</span>`;
              } else if (p.qtd_maduro > 0) {
                qualidadeInfo = `<br/><span style="font-size: 10pt; color: #c2410c;">TUDO MADURO</span>`;
              }
              return `
              <tr>
                <td>${p.nome}</td>
                <td>${p.quantidade.toFixed(2)} ${p.unidade || 'CX'}${qualidadeInfo}</td>
                <td>R$ ${p.custo.toFixed(2)}</td>
                <td><strong>R$ ${(p.custo_cx || 0).toFixed(2)}</strong></td>
                <td>${p.fornecedor}</td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gestão Pedidos</h3>
          <p className="text-sm text-gray-600">Gerencie envios de pedidos</p>
        </div>
        {temPermissaoComprador() && (
          <button
            onClick={handleAbrirCriarModal}
            className="flex items-center space-x-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
          >
            <span>➕</span>
            <span>Criar Envio</span>
          </button>
        )}
      </div>

      {/* Lista de Envios */}
      {envios.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">📋</div>
          <p className="text-gray-500">Nenhum envio criado ainda</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {envios.map(envio => (
            <div key={envio.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-lg mb-2">{envio.titulo}</h4>
              <p className="text-sm text-gray-600 mb-1">
                Pedidos: {envio.pedidos_ids.length}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Criado em: {new Date(envio.criado_em).toLocaleDateString('pt-BR')}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleVisualizar(envio)}
                  className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium"
                >
                  VISUALIZAR
                </button>
                {temPermissaoComprador() && (
                  <button
                    onClick={() => handleExcluirEnvio(envio)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm font-medium"
                  >
                    EXCLUIR
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Criar Envio */}
      {showCriarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold">Criar Novo Envio</h3>
              <button onClick={() => setShowCriarModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Título do Envio</label>
                <input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Ex: Envio Semana 1"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione uma Cotação (Opcional)
                </label>
                <select
                  value={cotacaoSelecionada}
                  onChange={(e) => setCotacaoSelecionada(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Nenhuma cotação vinculada</option>
                  {cotacoes.map(cotacao => (
                    <option key={cotacao.id} value={cotacao.id}>
                      {cotacao.nome || cotacao.numero || `Cotação ${cotacao.id.substring(0, 8)}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Vincule uma cotação para facilitar o rastreamento e controle do envio.
                </p>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione Pedidos Finalizados ({pedidosFinalizados.length})
                </label>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {pedidosFinalizados.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">Nenhum pedido finalizado disponível</p>
                  ) : (
                    pedidosFinalizados.map(pedido => (
                      <label key={pedido.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          checked={pedidosSelecionados.includes(pedido.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPedidosSelecionados([...pedidosSelecionados, pedido.id]);
                            } else {
                              setPedidosSelecionados(pedidosSelecionados.filter(id => id !== pedido.id));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">
                          Pedido #{pedido.numero} - {pedido.filial} - {pedido.produtos?.length || 0} produtos
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCriarModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCriarEnvio}
                  className="px-6 py-2 bg-green-700 text-white rounded hover:bg-green-800"
                >
                  Criar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sugestões de Ajuste */}
      {showSugestoesModal && sugestoes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-amber-600">⚠️ Ajuste de Pedidos Fracionados</h3>
                  <p className="text-sm text-gray-600">
                    O sistema identificou a melhor sugestão para cada produto
                  </p>
                </div>
                <button 
                  onClick={() => { setShowSugestoesModal(false); setSugestoes(null); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Explicação */}
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Como funciona:</strong> O sistema sugere a melhor opção para transformar cada quantidade fracionada em inteira. 
                  A sugestão prioriza ajustar o pedido com maior quantidade que seja ajustável. 
                  Você pode escolher outra opção clicando em "Ver alternativas".
                </p>
              </div>
              
              {/* Lista de produtos com sugestões */}
              <div className="space-y-4">
                {sugestoes.produtos.map((produto) => {
                  const sugestaoSelecionada = sugestoesSelecionadas[produto.produto_id];
                  const melhorOriginal = produto.melhor_sugestao;
                  
                  // Encontrar a sugestão ativa para cálculo do total
                  let sugestaoAtiva = null;
                  if (sugestaoSelecionada) {
                    if (melhorOriginal?.pedido_id === sugestaoSelecionada) {
                      sugestaoAtiva = melhorOriginal;
                    } else {
                      sugestaoAtiva = produto.alternativas?.find(s => s.pedido_id === sugestaoSelecionada);
                    }
                  } else {
                    sugestaoAtiva = melhorOriginal; // Default para melhor
                  }
                  
                  return (
                    <div key={produto.produto_id} className="border rounded-lg overflow-hidden">
                      {/* Header do produto */}
                      <div className="bg-gray-50 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-gray-800 text-lg">{produto.produto_nome}</span>
                            <div className="text-sm text-gray-600 mt-1">
                              Total atual: <span className="font-medium text-red-600">{produto.total_atual} cx</span>
                              {sugestaoAtiva && sugestaoSelecionada && (
                                <span> → Após ajuste: <span className="font-medium text-green-600">
                                  {(produto.total_atual + sugestaoAtiva.diferenca).toFixed(1)} cx
                                </span></span>
                              )}
                              {!sugestaoSelecionada && (
                                <span className="ml-2 text-amber-600">(nenhuma sugestão selecionada)</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {sugestaoSelecionada && (
                              <button
                                onClick={() => {
                                  setSugestoesSelecionadas(prev => ({
                                    ...prev,
                                    [produto.produto_id]: null
                                  }));
                                }}
                                className="px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Desmarcar sugestão"
                              >
                                ✕ Desmarcar
                              </button>
                            )}
                            <span className={`px-3 py-1 text-sm rounded-full font-medium ${
                              sugestaoSelecionada && sugestaoAtiva && Number.isInteger(produto.total_atual + sugestaoAtiva.diferenca)
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {sugestaoSelecionada && sugestaoAtiva && Number.isInteger(produto.total_atual + sugestaoAtiva.diferenca) 
                                ? '✓ Ficará inteiro' 
                                : '⚠ Fracionado'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* MELHOR SUGESTÃO (sempre visível e marcada por padrão) */}
                      {melhorOriginal && (
                        <div 
                          className={`p-4 ${sugestaoSelecionada === melhorOriginal.pedido_id ? 'bg-green-50' : 'bg-amber-50'} cursor-pointer border-b`}
                          onClick={() => {
                            setSugestoesSelecionadas(prev => ({
                              ...prev,
                              [produto.produto_id]: sugestaoSelecionada === melhorOriginal.pedido_id ? null : melhorOriginal.pedido_id
                            }));
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={sugestaoSelecionada === melhorOriginal.pedido_id}
                              onChange={() => {}}
                              className="mt-1 w-4 h-4 rounded"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${sugestaoSelecionada === melhorOriginal.pedido_id ? 'text-green-800' : 'text-amber-800'}`}>
                                  Pedido #{melhorOriginal.pedido_numero} - {melhorOriginal.filial}
                                </span>
                                <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
                                  SUGESTÃO DO SISTEMA
                                </span>
                                {sugestaoSelecionada === melhorOriginal.pedido_id && (
                                  <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                                    SELECIONADO
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-700 mt-1">
                                <span className="font-medium">{melhorOriginal.quantidade_atual}</span> → 
                                <span className={`font-medium ${melhorOriginal.diferenca > 0 ? 'text-green-700' : 'text-red-700'}`}> {melhorOriginal.quantidade_sugerida}</span>
                                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                  melhorOriginal.diferenca > 0 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {melhorOriginal.diferenca > 0 ? '+' : ''}{melhorOriginal.diferenca} cx
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{melhorOriginal.motivo}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* ALTERNATIVAS (ocultas por padrão) */}
                      {produto.alternativas && produto.alternativas.length > 0 && (
                        <div>
                          <button
                            onClick={() => {
                              const el = document.getElementById(`alternativas_${produto.produto_id}`);
                              const btn = document.getElementById(`btn_alt_${produto.produto_id}`);
                              if (el) {
                                el.classList.toggle('hidden');
                                btn.textContent = el.classList.contains('hidden') 
                                  ? `▶ Ver ${produto.alternativas.length} alternativa(s)` 
                                  : `▼ Ocultar alternativas`;
                              }
                            }}
                            id={`btn_alt_${produto.produto_id}`}
                            className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 flex items-center gap-2 bg-gray-50"
                          >
                            ▶ Ver {produto.alternativas.length} alternativa(s)
                          </button>
                          <div id={`alternativas_${produto.produto_id}`} className="hidden divide-y">
                            {produto.alternativas.map((alt, idx) => {
                              const isAltSelected = sugestaoSelecionada === alt.pedido_id;
                              return (
                                <div 
                                  key={idx}
                                  className={`p-4 ${isAltSelected ? 'bg-green-50' : 'bg-white'} hover:bg-gray-50 cursor-pointer`}
                                  onClick={() => {
                                    setSugestoesSelecionadas(prev => ({
                                      ...prev,
                                      [produto.produto_id]: isAltSelected ? null : alt.pedido_id
                                    }));
                                  }}
                                >
                                  <div className="flex items-start gap-3">
                                    <input
                                      type="checkbox"
                                      checked={isAltSelected}
                                      onChange={() => {}}
                                      className="mt-1 w-4 h-4 rounded"
                                    />
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className={`font-medium ${isAltSelected ? 'text-green-800' : 'text-gray-800'}`}>
                                          Pedido #{alt.pedido_numero} - {alt.filial}
                                        </span>
                                        {isAltSelected && (
                                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                                            SELECIONADO
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-700 mt-1">
                                        <span className="font-medium">{alt.quantidade_atual}</span> → 
                                        <span className={`font-medium ${alt.diferenca > 0 ? 'text-green-700' : 'text-red-700'}`}> {alt.quantidade_sugerida}</span>
                                        <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                          alt.diferenca > 0 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {alt.diferenca > 0 ? '+' : ''}{alt.diferenca} cx
                                        </span>
                                      </div>
                                      <p className="text-sm text-gray-600 mt-1">{alt.motivo}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Botões de ação */}
              <div className="mt-6 flex justify-between items-center border-t pt-4">
                <button
                  onClick={handlePularSugestoes}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Pular e criar envio sem ajustes
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowSugestoesModal(false); setSugestoes(null); }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleAplicarSugestoes}
                    disabled={aplicandoAjustes}
                    className={`px-6 py-2 rounded text-white font-medium ${
                      aplicandoAjustes
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-700 hover:bg-green-800'
                    }`}
                  >
                    {aplicandoAjustes ? 'Aplicando...' : 'Aplicar e Criar Envio'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Visualizar */}
      {showVisualizarModal && envioSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold">{envioSelecionado.titulo}</h3>
                  <p className="text-sm text-gray-600">
                    {envioSelecionado.pedidos_ids.length} pedido(s) • 
                    Criado por {envioSelecionado.criado_por_nome}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Botões de edição */}
                  {temPermissaoComprador() && !modoEdicao && (
                    <button
                      onClick={handleIniciarEdicao}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium"
                    >
                      ✏️ Editar Quantidades
                    </button>
                  )}
                  
                  {modoEdicao && (
                    <>
                      <button
                        onClick={handleCancelarEdicao}
                        disabled={salvandoEdicao}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm font-medium disabled:opacity-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleSalvarEdicao}
                        disabled={salvandoEdicao}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                      >
                        {salvandoEdicao ? 'Salvando...' : '✓ Salvar Alterações'}
                      </button>
                    </>
                  )}
                  
                  <button 
                    onClick={() => {
                      setShowVisualizarModal(false);
                      setModoEdicao(false);
                      setQuantidadesEditadas({});
                    }} 
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              {/* Banner de modo de edição */}
              {modoEdicao && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-800">
                    🔧 <strong>Modo de Edição:</strong> Expanda "VER FILIAIS" e edite as quantidades. As alterações serão refletidas na aba PEDIDOS.
                  </p>
                </div>
              )}
            </div>
            <div className="p-6">
              {loadingProdutos ? (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">⏳</div>
                  <p className="text-gray-600">Carregando produtos...</p>
                </div>
              ) : (
                <>
                  {usuario.tipo_usuario === 'Fornecedor' && produtosConsolidados.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">😔</div>
                      <p className="text-lg text-gray-700">Ops, infelizmente você não ganhou a cotação</p>
                    </div>
                  ) : (
                    <>
                      {/* Barra de Pesquisa */}
                      <div className="mb-4">
                        <div className="relative">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="🔍 Pesquisar produto..."
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {searchTerm && (
                            <button
                              onClick={() => setSearchTerm('')}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-4 flex justify-between items-center flex-wrap gap-3">
                        {/* Botões de filtro/ordenação - apenas para COMPRADOR */}
                        {temPermissaoComprador() && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => setFiltraMeiaCaixa(!filtraMeiaCaixa)}
                              className={`px-4 py-2 rounded flex items-center gap-2 font-medium transition-colors ${
                                filtraMeiaCaixa 
                                  ? 'bg-purple-100 text-purple-700 hover:bg-purple-200' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <span>{filtraMeiaCaixa ? '✓' : '○'}</span>
                              <span>FILTRA MEIA CAIXA</span>
                            </button>
                            <button
                              onClick={() => setOrdenacaoFornecedor(!ordenacaoFornecedor)}
                              className={`px-4 py-2 rounded flex items-center gap-2 font-medium transition-colors ${
                                ordenacaoFornecedor 
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              <span>{ordenacaoFornecedor ? '✓' : '○'}</span>
                              <span>ORDENAR POR FORNECEDOR</span>
                            </button>
                          </div>
                        )}
                        {usuario.tipo_usuario === 'Fornecedor' && <div></div>}
                        
                        {/* Botão de exportar */}
                        <button
                          onClick={exportarParaImpressao}
                          className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 flex items-center gap-2"
                        >
                          <span>🖨️</span>
                          <span>EXPORTAR PARA IMPRESSÃO</span>
                        </button>
                      </div>
                      
                      {/* Mensagem quando não há produtos após filtros */}
                      {getProdutosOrdenados().length === 0 ? (
                        <div className="text-center py-12">
                          <div className="text-4xl mb-4">🔍</div>
                          <p className="text-gray-600">Nenhum produto encontrado.</p>
                          {searchTerm && (
                            <p className="text-sm text-gray-500 mt-2">
                              Tente buscar por outro termo ou limpe o filtro.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">PRODUTO</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b">QUANTIDADE</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b">CUSTO (R$/KG)</th>
                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 border-b">CUSTO CX</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">FORNECEDOR</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getProdutosOrdenados().map((produto, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="px-4 py-3 text-sm border-b">{produto.nome}</td>
                                <td className="px-4 py-3 text-sm text-center border-b">
                                  {/* Em modo de edição, sempre mostrar botão VER FILIAIS para permitir adicionar em outras filiais */}
                                  {modoEdicao ? (
                                    <div>
                                      <div className="font-medium mb-2">{produto.quantidade.toFixed(2)} {produto.unidade || 'CX'}</div>
                                      <button
                                        onClick={() => {
                                          setProdutosExpandidos(prev => ({
                                            ...prev,
                                            [produto.produto_id]: !prev[produto.produto_id]
                                          }));
                                        }}
                                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium"
                                      >
                                        {produtosExpandidos[produto.produto_id] ? '▲' : '▼'} VER FILIAIS ({todasFiliais.length})
                                      </button>
                                      
                                      {/* Breakdown por filial quando expandido - TODAS as filiais */}
                                      {produtosExpandidos[produto.produto_id] && (
                                        <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded border border-gray-200">
                                          {todasFiliais.map((filial, idx) => {
                                            // Encontrar o pedido dessa filial
                                            const pedidoDaFilial = pedidos.find(p => 
                                              p.filial === filial && envioSelecionado.pedidos_ids.includes(p.id)
                                            );
                                            
                                            if (!pedidoDaFilial) return null;
                                            
                                            const key = `${pedidoDaFilial.id}_${produto.produto_id}`;
                                            const pedidoInfo = produto.pedidos.find(p => p.pedido_id === pedidoDaFilial.id);
                                            // Usar hasOwnProperty para verificar se a key existe, mesmo que o valor seja null
                                            const quantidadeAtual = quantidadesEditadas.hasOwnProperty(key) 
                                              ? quantidadesEditadas[key] 
                                              : (pedidoInfo?.quantidade || 0);
                                            const meiaCaixaInfo = meiaCaixaEditada[key] || {};
                                            
                                            // Verificar se tem meia caixa (quantidade decimal)
                                            const temMeiaCaixa = quantidadeAtual !== null && quantidadeAtual % 1 !== 0;
                                            
                                            return (
                                              <div key={idx} className="bg-white p-3 rounded border space-y-2">
                                                <div className="flex items-center justify-between">
                                                  <div className="text-xs flex-1">
                                                    <div className="font-medium text-gray-700">{filial}</div>
                                                    <div className="text-gray-500">Pedido: {pedidoDaFilial.numero}</div>
                                                  </div>
                                                  <div className="text-xs font-medium text-gray-900 flex items-center gap-2">
                                                    <input
                                                      type="number"
                                                      step="0.5"
                                                      min="0"
                                                      placeholder="0"
                                                      value={quantidadeAtual === null || quantidadeAtual === 0 ? '' : quantidadeAtual}
                                                      onChange={(e) => handleQuantidadeChange(
                                                        pedidoDaFilial.id,
                                                        produto.produto_id,
                                                        e.target.value
                                                      )}
                                                      className="w-20 px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                      title="Deixe vazio para remover. Apenas números inteiros ou meios (0.5, 1.5, 2.5...)"
                                                    />
                                                    <span>{produto.unidade || 'CX'}</span>
                                                  </div>
                                                </div>
                                                
                                                {/* Mostrar campos de meia caixa apenas se quantidade tem decimal */}
                                                {temMeiaCaixa && (
                                                  <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                                                    <div className="text-xs font-semibold text-purple-700">📦 Informações de Meia Caixa:</div>
                                                    <div className="space-y-1">
                                                      <label className="flex items-center gap-2 text-xs">
                                                        <input
                                                          type="radio"
                                                          name={`meia_cx_${key}`}
                                                          value="Mandar meia cx"
                                                          checked={meiaCaixaInfo.pediu_meia_cx === 'Mandar meia cx'}
                                                          onChange={(e) => handleMeiaCaixaChange(pedidoDaFilial.id, produto.produto_id, 'pediu_meia_cx', e.target.value)}
                                                          className="text-blue-600"
                                                        />
                                                        Mandar meia cx
                                                      </label>
                                                      <label className="flex items-center gap-2 text-xs">
                                                        <input
                                                          type="radio"
                                                          name={`meia_cx_${key}`}
                                                          value="Não mandar meia cx"
                                                          checked={meiaCaixaInfo.pediu_meia_cx === 'Não mandar meia cx'}
                                                          onChange={(e) => handleMeiaCaixaChange(pedidoDaFilial.id, produto.produto_id, 'pediu_meia_cx', e.target.value)}
                                                          className="text-blue-600"
                                                        />
                                                        Não mandar meia cx
                                                      </label>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    /* Modo visualização normal */
                                    produto.pedidos && produto.pedidos.length === 1 ? (
                                      <div>
                                        <div className="text-xs text-gray-600 font-medium mb-1">{produto.pedidos[0].filial}</div>
                                        <div className="font-medium">{produto.quantidade.toFixed(2)} {produto.unidade || 'CX'}</div>
                                      </div>
                                    ) : produto.pedidos && produto.pedidos.length > 1 ? (
                                      /* Se produto consolidado de múltiplos pedidos */
                                      <div>
                                        <div className="font-medium mb-2">{produto.quantidade.toFixed(2)} {produto.unidade || 'CX'}</div>
                                        <button
                                          onClick={() => {
                                            setProdutosExpandidos(prev => ({
                                              ...prev,
                                              [produto.produto_id]: !prev[produto.produto_id]
                                            }));
                                          }}
                                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium"
                                        >
                                          {produtosExpandidos[produto.produto_id] ? '▲' : '▼'} VER FILIAIS ({produto.pedidos.length})
                                        </button>
                                        
                                        {/* Breakdown por filial quando expandido */}
                                        {produtosExpandidos[produto.produto_id] && (
                                          <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded border border-gray-200">
                                            {produto.pedidos.map((pedidoInfo, idx) => (
                                              <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border text-left">
                                                <div className="text-xs flex-1">
                                                  <div className="font-medium text-gray-700">{pedidoInfo.filial}</div>
                                                  <div className="text-gray-500">Pedido: {pedidoInfo.pedido_numero}</div>
                                                </div>
                                                <div className="text-xs font-medium text-gray-900">
                                                  {pedidoInfo.quantidade.toFixed(2)} {produto.unidade || 'CX'}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      /* Fallback se não houver info de pedidos */
                                      <div className="font-medium">{produto.quantidade.toFixed(2)} {produto.unidade || 'CX'}</div>
                                    )
                                  )}
                                  
                                  {/* Informações de qualidade */}
                                  {produto.qtd_verde > 0 && produto.qtd_maduro > 0 ? (
                                    <div className="text-xs text-gray-600 mt-2">
                                      <span className="text-green-700">VERDE: {produto.qtd_verde.toFixed(2)}</span>
                                      {' • '}
                                      <span className="text-orange-700">MADURO: {produto.qtd_maduro.toFixed(2)}</span>
                                    </div>
                                  ) : produto.qtd_verde > 0 ? (
                                    <div className="text-xs text-green-700 mt-2">TUDO VERDE</div>
                                  ) : produto.qtd_maduro > 0 ? (
                                    <div className="text-xs text-orange-700 mt-2">TUDO MADURO</div>
                                  ) : null}
                                </td>
                                <td className="px-4 py-3 text-sm text-center border-b">R$ {produto.custo.toFixed(2)}</td>
                                <td className="px-4 py-3 text-sm text-center border-b">
                                  <span className="font-medium text-green-700">R$ {(produto.custo_cx || 0).toFixed(2)}</span>
                                </td>
                                <td className="px-4 py-3 text-sm border-b">{produto.fornecedor}</td>
                              </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}


    </div>
  );
};


// ===========================
// PROMOÇÃO TAB COMPONENT
// ===========================

export default GestaoPedidosTab;
